import Footer from "@/components/Footer";
import { Header } from "@/components/landing/Header";
import { Suspense } from "react";

const TermsAndConditions = () => {
    return (
        <>
            <Header />
            <main className="flex min-h-full items-center justify-center bg-stone-50 dark:bg-stone-950 px-4 py-24">
                <div className="container max-w-4xl mx-auto px-4">
                    <h1 className="text-3xl font-bold mb-8 dark:text-white">Terms and Conditions</h1>

                    <div className="text-sm text-stone-500 dark:text-stone-500 mb-8">Last Updated: February 16, 2025</div>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">1. Introduction</h2>
                        <p className="mb-4 dark:text-stone-500">Welcome to IELTS With AI ("we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of our website and IELTS writing practice services (the "Service"). By accessing or using our Service, you agree to be bound by these Terms.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">2. Company Information</h2>
                        <p className="mb-4 dark:text-stone-500">IELTS With AI is registered and operates under the laws of Norway. Our services are available internationally, subject to these Terms and applicable local laws.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">3. Service Description</h2>
                        <p className="mb-4 dark:text-stone-500">We provide AI-powered IELTS writing practice and evaluation services. The Service includes:</p>
                        <ul className="list-disc pl-8 mb-4 dark:text-stone-500">
                            <li className="mb-2">Access to IELTS-style writing tasks</li>
                            <li className="mb-2">AI-powered essay evaluation</li>
                            <li className="mb-2">Detailed feedback and scoring</li>
                            <li className="mb-2">Progress tracking</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">4. User Accounts and Registration</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">4.1. To use our Service, you must create an account and provide accurate, current, and complete information.</p>
                            <p className="mb-2">4.2. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</p>
                            <p className="mb-2">4.3. One account is for individual use only. Sharing accounts is prohibited.</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">5. Payment and Refunds</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">5.1. The Service is available for a one-time payment of $19 USD.</p>
                            <p className="mb-2">5.2. Payments are processed securely through Stripe, our payment processor.</p>
                            <p className="mb-2">5.3. Refund Policy:</p>
                            <ul className="list-disc pl-8">
                                <li className="mb-2">You may request a refund within 30 days of purchase if you are not satisfied with the Service</li>
                                <li className="mb-2">Refund requests must be submitted via email</li>
                                <li className="mb-2">Refunds will be processed through the original payment method</li>
                                <li className="mb-2">We reserve the right to deny refunds in cases of apparent abuse or excessive use of the Service</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">6. Usage Guidelines</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">6.1. Our Service is designed to provide unlimited practice opportunities for IELTS preparation under appropriate usage.</p>
                            <p className="mb-2">6.2. To ensure quality service for all users, we monitor account activity. If your usage reaches high volumes (approximately 1,000 evaluations), we may reach out to verify your needs and ensure proper service utilization.</p>
                            <p className="mb-2">6.3. We're happy to provide additional evaluations for users who demonstrate genuine need for extended practice.</p>
                            <p className="mb-2">6.4. Automated submissions or the use of bots is strictly prohibited.</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">7. Intellectual Property and Content Usage</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">7.1. You retain ownership of your essay content submitted to the Service.</p>
                            <p className="mb-2">7.2. You may share your essays and received feedback with others.</p>
                            <p className="mb-2">7.3. We do not claim ownership of user-generated content but reserve the right to use anonymized data for service improvement.</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">8. Data Protection and Privacy</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">8.1. We collect and store:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Email addresses for account management</li>
                                <li className="mb-2">Essay content for service provision</li>
                                <li className="mb-2">Payment information (processed by Stripe)</li>
                            </ul>
                            <p className="mb-2">8.2. Essay content is:</p>
                            <ul className="list-disc pl-8 mb-4">
                                <li className="mb-2">Stored indefinitely for user reference</li>
                                <li className="mb-2">Processed using OpenAI's services</li>
                                <li className="mb-2">Not used for AI training purposes</li>
                            </ul>
                            <p className="mb-2">8.3. We comply with applicable data protection regulations and maintain appropriate security measures.</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">9. Disclaimers and Limitations</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">9.1. The Service is provided "as is" without warranties of any kind.</p>
                            <p className="mb-2">9.2. We do not guarantee specific IELTS test scores or results.</p>
                            <p className="mb-2">9.3. We are not responsible for:</p>
                            <ul className="list-disc pl-8">
                                <li className="mb-2">The use of AI-generated content in official IELTS examinations</li>
                                <li className="mb-2">Technical issues beyond our reasonable control</li>
                                <li className="mb-2">Loss of data or content</li>
                            </ul>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">10. Prohibited Activities</h2>
                        <p className="mb-4 dark:text-stone-500">You agree not to:</p>
                        <ul className="list-disc pl-8 dark:text-stone-500">
                            <li className="mb-2">Share account access with others</li>
                            <li className="mb-2">Use automated tools or bots</li>
                            <li className="mb-2">Attempt to circumvent usage limitations</li>
                            <li className="mb-2">Use the Service for any unlawful purpose</li>
                            <li className="mb-2">Interfere with the proper operation of the Service</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">11. Termination</h2>
                        <div className="mb-4 dark:text-stone-500">
                            <p className="mb-2">11.1. We reserve the right to suspend or terminate accounts that violate these Terms.</p>
                            <p className="mb-2">11.2. You may terminate your account at any time by contacting us.</p>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">12. Changes to Terms</h2>
                        <p className="mb-4 dark:text-stone-500">We may modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of modified Terms.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">13. Governing Law</h2>
                        <p className="mb-4 dark:text-stone-500">These Terms are governed by the laws of Norway, without regard to conflict of law principles.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">14. Contact Information</h2>
                        <p className="mb-4 dark:text-stone-500">For questions about these Terms or the Service, please contact us at contact@ieltswith.ai.</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 dark:text-white">15. Severability</h2>
                        <p className="mb-4 dark:text-stone-500">If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in effect.</p>
                    </section>
                </div>
            </main>
            <Suspense fallback={<div className="h-16" />}>
                <Footer />
            </Suspense>
        </>
    );
};

export default TermsAndConditions;
